import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Dashboard.css';
import { 
  FiUsers, 
  FiUserCheck, 
  FiShoppingBag, 
  FiActivity, 
  FiBarChart2, 
  FiSettings,
  FiLogOut,
  FiBell,
  FiSearch,
  FiTrendingUp,
  FiShield,
  FiClock,
  FiHeadphones,
  FiCpu,
  FiGlobe,
  FiDatabase,
  FiLock,
  FiAlertCircle
} from 'react-icons/fi';
import { HiOutlineSparkles } from 'react-icons/hi';

interface DashboardStats {
  total_users: number;
  total_active_merchants: number;
  pending_kyc: number;
  today_transactions: number;
  today_volume: number;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

// Role-based permissions configuration
const rolePermissions: Record<string, string[]> = {
  super_admin: [
    'dashboard', 'users', 'kyc', 'merchants', 'transactions', 'activity-log', 'customer-service', 'settings'
  ],
  kyc_officer: [
    'dashboard', 'users', 'kyc', 'customer-service'
  ],
  customer_support: [
    'dashboard', 'users', 'transactions', 'customer-service'
  ],
  compliance_officer: [
    'dashboard', 'users', 'kyc', 'transactions', 'activity-log'
  ],
  merchant_manager: [
    'dashboard', 'merchants', 'transactions'
  ],
  auditor: [
    'dashboard', 'users', 'merchants', 'transactions', 'activity-log'
  ],
};

// Menu items with required permissions
const menuItems = [
  { 
    icon: <FiBarChart2 />, 
    label: 'Dashboard', 
    path: '/', 
    permissionKey: 'dashboard',
    description: 'Overview & Analytics',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    glow: '#667eea'
  },
  { 
    icon: <FiUsers />, 
    label: 'Users', 
    path: '/users', 
    permissionKey: 'users',
    description: 'Manage all registered users',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    glow: '#f093fb'
  },
  { 
    icon: <FiUserCheck />, 
    label: 'KYC Verification', 
    path: '/kyc', 
    permissionKey: 'kyc',
    description: 'Verify customers & merchants',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    glow: '#4facfe'
  },
  { 
    icon: <FiShoppingBag />, 
    label: 'Merchants', 
    path: '/merchants', 
    permissionKey: 'merchants',
    description: 'Manage merchant accounts',
    gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    glow: '#fa709a'
  },
  { 
    icon: <FiActivity />, 
    label: 'Transactions', 
    path: '/transactions', 
    permissionKey: 'transactions',
    description: 'Monitor all transactions',
    gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    glow: '#a8edea'
  },
  { 
    icon: <FiClock />, 
    label: 'Activity Log', 
    path: '/activity-log', 
    permissionKey: 'activity-log',
    description: 'Track employee actions',
    gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    glow: '#ff9a9e'
  },
  { 
    icon: <FiHeadphones />, 
    label: 'Customer Service', 
    path: '/customer-service', 
    permissionKey: 'customer-service',
    description: 'Support & tickets',
    gradient: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
    glow: '#a1c4fd'
  },
  { 
    icon: <FiSettings />, 
    label: 'Settings', 
    path: '/settings', 
    permissionKey: 'settings',
    description: 'System configuration',
    gradient: 'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)',
    glow: '#d4fc79'
  },
];

// Skeleton Card Component
const SkeletonCard: React.FC = () => (
  <div className="stat-card glass skeleton-card">
    <div className="skeleton-icon"></div>
    <div className="skeleton-info">
      <div className="skeleton-value"></div>
      <div className="skeleton-title"></div>
    </div>
  </div>
);

const Dashboard: React.FC = () => {
  const { token, employee, logout } = useAuth();
  const navigate = useNavigate();
  
  // Load cached stats immediately from sessionStorage
  const [stats, setStats] = useState<DashboardStats | null>(() => {
    const cached = sessionStorage.getItem('dashboard_stats');
    return cached ? JSON.parse(cached) : null;
  });
  
  const [loading, setLoading] = useState(!stats);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState<{ show: boolean; message: string }>({ show: false, message: '' });
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastFetchRef = useRef<number>(0);
  const CACHE_DURATION = 60000; // 60 seconds cache

  useEffect(() => {
    const now = Date.now();
    const lastFetch = lastFetchRef.current;
    
    // Only fetch if no cache or cache expired
    if (!stats || (now - lastFetch) > CACHE_DURATION) {
      fetchDashboardStats();
    } else {
      setLoading(false);
    }
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchDashboardStats = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;
    lastFetchRef.current = Date.now();
    
    try {
      // Fetch total users from the working stats endpoint
      const usersStatsResponse = await axios.get(`${API_URL}/employees/users/stats`, {
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Fetch dashboard stats for other metrics
      const dashboardResponse = await axios.get(`${API_URL}/employees/dashboard/stats`, {
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (usersStatsResponse.data && usersStatsResponse.data.success) {
        const dashboardData = dashboardResponse.data?.data?.summary || {};
        
        const combinedStats = {
          total_users: usersStatsResponse.data.data.total_users || 0,
          total_active_merchants: dashboardData.total_active_merchants || 0,
          pending_kyc: dashboardData.pending_kyc || 0,
          today_transactions: dashboardData.today_transactions || 0,
          today_volume: dashboardData.today_volume || 0
        };
        
        setStats(combinedStats);
        sessionStorage.setItem('dashboard_stats', JSON.stringify(combinedStats));
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Failed to fetch dashboard stats:', error);
        if (!stats) {
          setStats({
            total_users: 0,
            total_active_merchants: 0,
            pending_kyc: 0,
            today_transactions: 0,
            today_volume: 0
          });
        }
      }
    } finally {
      if (controller === abortControllerRef.current) {
        setLoading(false);
      }
    }
  };

  const handleNavigation = (path: string, permissionKey: string) => {
    const userRole = employee?.role || '';
    const hasPermission = rolePermissions[userRole]?.includes(permissionKey) || false;
    
    if (hasPermission || permissionKey === 'dashboard') {
      navigate(path);
    } else {
      setAccessDenied({
        show: true,
        message: `You don't have permission to access ${menuItems.find(i => i.permissionKey === permissionKey)?.label}. Please contact your administrator.`
      });
      setTimeout(() => {
        setAccessDenied({ show: false, message: '' });
      }, 3000);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userRole = employee?.role || '';
  const filteredMenuItems = menuItems.filter(item => {
    if (item.permissionKey === 'dashboard') return true;
    return rolePermissions[userRole]?.includes(item.permissionKey) || false;
  });

  const statCards = [
    { 
      title: 'Total Users', 
      value: stats?.total_users || 0, 
      icon: <FiUsers />, 
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      subtitle: 'Registered accounts',
      trend: '+12%',
      permissionKey: 'users'
    },
    { 
      title: 'Active Merchants', 
      value: stats?.total_active_merchants || 0, 
      icon: <FiShoppingBag />, 
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      subtitle: 'Verified businesses',
      trend: '+8%',
      permissionKey: 'merchants'
    },
    { 
      title: 'Pending KYC', 
      value: stats?.pending_kyc || 0, 
      icon: <FiUserCheck />, 
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      subtitle: 'Awaiting verification',
      trend: '-3%',
      permissionKey: 'kyc'
    },
    { 
      title: "Today's Volume", 
      value: `$${stats?.today_volume?.toLocaleString() || 0}`, 
      icon: <FiTrendingUp />, 
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      subtitle: 'Transaction total',
      trend: '+23%',
      permissionKey: 'transactions'
    },
  ];

  const filteredStatCards = statCards.filter(card => 
    rolePermissions[userRole]?.includes(card.permissionKey) || userRole === 'super_admin'
  );

  const aiInsights = [
    { icon: <FiCpu />, title: 'AI Fraud Detection', status: 'Active', description: 'Real-time monitoring' },
    { icon: <FiGlobe />, title: 'Network Status', status: 'Optimal', description: '99.99% uptime' },
    { icon: <FiDatabase />, title: 'Data Sync', status: 'Live', description: 'Blockchain verified' },
    { icon: <FiLock />, title: 'Security Level', status: 'Tier 4', description: 'Military grade' },
  ];

  if (loading && !stats) {
    return (
      <div className="dashboard">
        <div className="ai-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
          <div className="grid-overlay"></div>
        </div>
        <aside className="sidebar glass">
          <div className="sidebar-header">
            <div className="logo">
              <span className="logo-icon">🇱🇷</span>
              <span>Digital Liberia</span>
              <HiOutlineSparkles className="ai-sparkle" />
            </div>
          </div>
          <div className="sidebar-nav">
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="nav-item-skeleton"></div>
            ))}
          </div>
        </aside>
        <main className="main-content">
          <div className="stats-grid">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {accessDenied.show && (
        <div className="access-denied-toast">
          <FiAlertCircle />
          <span>{accessDenied.message}</span>
        </div>
      )}

      <div className="ai-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="grid-overlay"></div>
      </div>

      <aside className="sidebar glass">
        <div className="sidebar-header">
          <div className="logo">
            <span className="logo-icon">🇱🇷</span>
            <span>Digital Liberia</span>
            <HiOutlineSparkles className="ai-sparkle" />
          </div>
          <div className="ai-badge">AI Powered</div>
        </div>

        <nav className="sidebar-nav">
          {filteredMenuItems.map((item, index) => (
            <button
              key={index}
              className={`nav-item ${window.location.pathname === item.path ? 'active' : ''}`}
              onClick={() => handleNavigation(item.path, item.permissionKey)}
              onMouseEnter={() => setHoveredCard(item.label)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                '--glow-color': item.glow,
              } as React.CSSProperties}
            >
              <span className="nav-icon" style={{ background: item.gradient }}>
                {item.icon}
              </span>
              <div className="nav-text">
                <span className="nav-label">{item.label}</span>
                <span className="nav-description">{item.description}</span>
              </div>
              {hoveredCard === item.label && <div className="nav-glow" style={{ background: item.gradient }}></div>}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar-glow">
              <div className="user-avatar">
                {employee?.email?.charAt(0)?.toUpperCase() || 'E'}
              </div>
            </div>
            <div className="user-details">
              <div className="user-name">{employee?.email?.split('@')[0] || 'Employee'}</div>
              <div className="user-role">
                <span className="role-dot"></span>
                {employee?.role?.replace(/_/g, ' ') || 'Staff'}
              </div>
            </div>
          </div>
          <button onClick={handleLogout} className="logout-btn">
            <FiLogOut /> Sign Out
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="top-bar glass">
          <div className="search-bar">
            <FiSearch />
            <input type="text" placeholder="Search anything..." />
            <span className="search-shortcut">⌘K</span>
          </div>
          <div className="top-actions">
            <button className="notification-btn">
              <FiBell />
              <span className="notification-dot"></span>
            </button>
            <div className="ai-status">
              <div className="ai-pulse"></div>
              <span>AI Assistant Active</span>
            </div>
          </div>
        </header>

        <div className="content-area">
          <div className="page-header">
            <div>
              <h1>Welcome back, {employee?.email?.split('@')[0] || 'Employee'}!</h1>
              <p>Here's what's happening with your digital ecosystem today.</p>
            </div>
            <div className="ai-insight-badge">
              <HiOutlineSparkles />
              <span>AI Insights Available</span>
            </div>
          </div>

          <div className="stats-grid">
            {filteredStatCards.map((stat, index) => (
              <div key={index} className="stat-card glass" style={{ background: stat.gradient + ', rgba(10, 10, 20, 0.4)' }}>
                <div className="stat-icon-wrapper">
                  {stat.icon}
                  <div className="stat-trend positive">{stat.trend}</div>
                </div>
                <div className="stat-info">
                  <h3>{typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}</h3>
                  <p>{stat.title}</p>
                  <span className="stat-subtitle">{stat.subtitle}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="ai-insights glass">
            <div className="insights-header">
              <HiOutlineSparkles />
              <h3>AI Intelligence</h3>
              <span className="live-badge">LIVE</span>
            </div>
            <div className="insights-grid">
              {aiInsights.map((insight, index) => (
                <div key={index} className="insight-card">
                  <div className="insight-icon">{insight.icon}</div>
                  <div className="insight-info">
                    <h4>{insight.title}</h4>
                    <p>{insight.description}</p>
                  </div>
                  <div className="insight-status">{insight.status}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="quick-actions">
            <h2>Quick Access</h2>
            <div className="actions-grid">
              {filteredMenuItems.slice(1).map((item, index) => (
                <div
                  key={index}
                  className="action-card glass"
                  onClick={() => handleNavigation(item.path, item.permissionKey)}
                  style={{
                    '--card-gradient': item.gradient,
                    '--card-glow': item.glow,
                  } as React.CSSProperties}
                >
                  <div className="action-icon" style={{ background: item.gradient }}>
                    {item.icon}
                  </div>
                  <div className="action-info">
                    <h3>{item.label}</h3>
                    <p>{item.description}</p>
                  </div>
                  <div className="action-arrow">→</div>
                </div>
              ))}
            </div>
          </div>

          <div className="recent-activity glass">
            <div className="activity-header">
              <h2>Recent Activity</h2>
              <button className="view-all">View All →</button>
            </div>
            <div className="activity-list">
              <div className="activity-item">
                <div className="activity-icon-wrapper" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
                  <span>📝</span>
                </div>
                <div className="activity-details">
                  <p className="activity-title">KYC application submitted</p>
                  <p className="activity-subtitle">John Doe completed identity verification</p>
                  <span className="activity-time">2 minutes ago</span>
                </div>
                <div className="activity-badge pending">Pending Review</div>
              </div>
              <div className="activity-item">
                <div className="activity-icon-wrapper" style={{ background: 'linear-gradient(135deg, #f093fb, #f5576c)' }}>
                  <span>💰</span>
                </div>
                <div className="activity-details">
                  <p className="activity-title">Transaction completed</p>
                  <p className="activity-subtitle">$1,250.00 sent to ABC Store</p>
                  <span className="activity-time">15 minutes ago</span>
                </div>
                <div className="activity-badge completed">Completed</div>
              </div>
              <div className="activity-item">
                <div className="activity-icon-wrapper" style={{ background: 'linear-gradient(135deg, #fa709a, #fee140)' }}>
                  <span>🏪</span>
                </div>
                <div className="activity-details">
                  <p className="activity-title">New merchant registration</p>
                  <p className="activity-subtitle">ABC Store joined the platform</p>
                  <span className="activity-time">1 hour ago</span>
                </div>
                <div className="activity-badge success">Approved</div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
